/**
 * May need to wrap token before
 */
import { Pool } from '../../logic/pools/Pool'
import { Token } from '../../logic/token/token'
import { Address, Wei } from '../../utils/types'
import {
  PoolWithData,
  ZeroExQuote,
  TokenWithData,
  ZeroExRoute,
} from '../api-types'
import axios from 'axios'
import Web3 from 'web3'
import { oracleApi } from '../oracle/oracle-client'
import { multiply } from '../../logic/weis/wei-calculate'
import { maxDepositSlippage } from '../../config'
import { floorWei, fromWei } from '../../utils/static-web3'
import { PoolApi } from '../web3/pool-api'
import { Amounts } from '../../logic/token/amounts'

const REGRESSION_MODEL = {
  parameters: {
    a: 8.678182360902874e-9,
    b: 3.038797251596977e-15,
    c: 0.000838019407981208,
  },
  softener_coefficient: 0.8,
}

interface LimitingAmounts {
  limitingToken: Token
  expectedShare: Wei
  amounts: Amounts
}

export class ZeroExApi {
  constructor(
    public web3: Web3,
    public account: Address,
    public chainId: number,
    public proxyAddress: Address
  ) {}

  /**
   * We join the pool with one token and one amount.
   * Before calling this function, we already have some ZeroEx[] quotations,
   * so we have a bag of token to join the pool.
   * This function calculates the amount of each token will be engaged (amounts), and which token (limitingToken) will
   * minimize the number of pool share (expectedShare) we can mint with `amounts`
   * @param pool
   * @param quote
   * @param token
   * @param amount
   */
  async getLimitingAmountsForSwap(
    pool: Pool,
    quote: ZeroExQuote[],
    token: Token,
    amount: Wei
  ): Promise<LimitingAmounts> {
    let expectedCalculatedShare: number | undefined = undefined
    let amountsInReference: Wei[] = []
    let limitingToken: Token | undefined = undefined
    const poolApi = new PoolApi(this.web3, this.account, pool.address)

    // This is our working object
    const tokensAndAmounts: Map<Token, Wei> = new Map()

    // We will consume joining token for each token of the pool
    let tokenAmountConsumed = 0
    for (let tokenQuote of quote) {
      const tokenBought = pool.tokens.find(
        t => t.address === tokenQuote.buyTokenAddress
      )!
      const amountBought = tokenQuote.buyAmount
      tokenAmountConsumed += Number(tokenQuote.sellAmount)

      // The token we bought on aggregator will be used to enter the pool
      tokensAndAmounts.set(tokenBought, amountBought)
    }

    // However, in the case we entered with Weth in Weth-usdc-wbtc pool, then we decrease amount of weth used for pool entering
    if (pool.tokens.some(t => t.address === token.address)) {
      const leaderAvailableAmount = Number(amount) - tokenAmountConsumed
      tokensAndAmounts.set(token, floorWei(leaderAvailableAmount))
    }

    // Now having the right amount of entering tokens, we can calculate the amount of shares minted
    for (let [tokenBought, amountBought] of Array.from(
      tokensAndAmounts.entries()
    )) {
      const [share, amountsIn] = await poolApi.viewMultiAssetJoinShare(
        tokenBought,
        amountBought
      )

      if (
        expectedCalculatedShare === undefined ||
        Number(share) < expectedCalculatedShare
      ) {
        limitingToken = tokenBought
        expectedCalculatedShare = Number(share)
        amountsInReference = amountsIn
      }
    } // end of for

    return {
      limitingToken: limitingToken!,
      expectedShare: floorWei(expectedCalculatedShare!),
      amounts: Amounts.fromArray(pool, amountsInReference),
    }
  }

  static quotesToRoutes(quotes: ZeroExQuote[]): ZeroExRoute[] {
    return quotes.map(quote => {
      return {
        ...quote,
        data: quote.swapCallData,
      }
    })
  }
}

export async function get0xQuote(
  token: Token,
  pool: PoolWithData,
  amount: Wei,
  slippage:number
): Promise<ZeroExQuote[]> {
  const routes = await getConversionRoutes(
    token.address,
    token.decimals,
    pool,
    token,
    amount,
    slippage
  )

  return routes.map(rawQuoteZeroEx => {
    return {
      buyAmount: rawQuoteZeroEx.data.buyAmount,
      guaranteedBuyAmount: multiply(
        rawQuoteZeroEx.data.buyAmount,
        rawQuoteZeroEx.data.guaranteedPrice / rawQuoteZeroEx.data.price
      ),
      sellAmount: rawQuoteZeroEx.data.sellAmount,
      buyTokenAddress: rawQuoteZeroEx.data.buyTokenAddress,
      sellTokenAddress: rawQuoteZeroEx.data.sellTokenAddress,
      spender: rawQuoteZeroEx.data.allowanceTarget,
      swapTarget: rawQuoteZeroEx.data.to,
      swapCallData: rawQuoteZeroEx.data.data,
    }
  })
}

export function isTokenInPool(token: Token, pool: Pool) {
  return pool.tokens.some(t => t.address === token.address)
}

// TODO: use BgNumber / BigInt instead of Number
export async function getConversionRoutes(
  externalSellTokenAddress: string,
  externalSellTokenDecimals: number,
  pool: PoolWithData,
  token: Token,
  amount: Wei,
  slippagePercentage: number
): Promise<Array<any>> {
  const tokensToBuy = pool.tokens.filter(t => t.address !== token.address)

  const denormEstimatedShares: Map<string, number> = pool.tokens.reduce(
    (acc, t) => {
      let denormEstimatedShare =
        fromWei(t.balance, t.decimals) * oracleApi.getPrice(t.symbol)
      if (t.address !== externalSellTokenAddress) {
        const amountToBuy =
          (Number(amount) / 10 ** externalSellTokenDecimals) * t.percent // rough amount estimation
        const slippage = estimateSlippage(amountToBuy)
        denormEstimatedShare *= slippage
      }
      acc.set(t.address, denormEstimatedShare)
      return acc
    },
    new Map<string, number>()
  )

  const weightSum = Array.from(denormEstimatedShares.values()).reduce(
    (acc, v) => acc + v,
    0
  )
  
  let amountSum = BigInt(0)
  const normEstimatedShares: Map<string, bigint> = new Map<string, bigint>()
  pool.tokens.slice(1).reduce((acc, t) => {
    const _amount = BigInt(Math.floor(
      (denormEstimatedShares.get(t.address)! / weightSum) * Number(amount)
      ))
      normEstimatedShares.set(t.address, _amount)
      amountSum += _amount
      return acc
    }, 0)

  normEstimatedShares.set(
    pool.tokens.at(0)!.address,
    BigInt(amount) - BigInt(amountSum)
  )

  const promises = tokensToBuy.map(t => {
    const sellAmountForThisToken = normEstimatedShares
      .get(t.address)!
      .toString()
    const url = getOxQuoteUrl(
      token.address,
      t,
      sellAmountForThisToken,
      slippagePercentage
    )
    return axios.get(url)
  })
  return Promise.all(promises)
}

export function getOxQuoteUrl(
  sellTokenAddress: Address,
  buyToken: Token,
  amount: Wei,
  slippagePercentage: number
) {
  const sellSymbol = sellTokenAddress
  const buySymbol = buyToken.address

  return `https://polygon.api.0x.org/swap/v1/quote?buyToken=${buySymbol}&sellToken=${sellSymbol}&sellAmount=${amount}&slippagePercentage=${slippagePercentage}`
}

function estimateSlippage(amount: number): number {
  const amountToTradeApproximation = amount
  const forecastedSlippage =
    (REGRESSION_MODEL.parameters.a * amountToTradeApproximation +
      REGRESSION_MODEL.parameters.b * amountToTradeApproximation ** 2 +
      REGRESSION_MODEL.parameters.c) *
    REGRESSION_MODEL.softener_coefficient
  return 1 + forecastedSlippage
}
